import type { Rng } from './rng';
import { defaultRng } from './rng';
import { generateEmail, generateUaePhone } from './primitives';

/**
 * UAE / GCC sample names and company patterns.
 * Names are intentionally generic (placed-after-prefix surnames are common
 * in the UAE; we shuffle them for variety). All Arabic strings are valid
 * UTF-8 and use Modern Standard Arabic glyphs.
 */

export interface SampleCompany {
  /** English legal name. */
  nameEn: string;
  /** Arabic legal name (MSA). */
  nameAr: string;
  /** kebab-case slug used to derive emails / domains. */
  slug: string;
  /** Optional industry hint (used for line-item generation later). */
  industry:
    | 'trading'
    | 'logistics'
    | 'real_estate'
    | 'banking'
    | 'energy'
    | 'telecom'
    | 'aviation'
    | 'hospitality'
    | 'tech'
    | 'retail'
    | 'construction'
    | 'healthcare';
}

export interface SamplePerson {
  givenName: string;
  familyName: string;
  givenNameAr: string;
  familyNameAr: string;
  /** 'm' | 'f' for greeting agreement only — not used in UI yet. */
  gender: 'm' | 'f';
}

export const COMPANIES: SampleCompany[] = [
  { nameEn: 'Dubai Trading LLC',         nameAr: 'شركة دبي للتجارة',          slug: 'dubaitrading',     industry: 'trading'      },
  { nameEn: 'Etihad Logistics FZE',       nameAr: 'الاتحاد للخدمات اللوجستية', slug: 'etihad-logistics', industry: 'logistics'    },
  { nameEn: 'Emirates Real Estate JLT',   nameAr: 'الإمارات للعقارات',         slug: 'emirates-realty',  industry: 'real_estate'  },
  { nameEn: 'Al Mansoori Group LLC',      nameAr: 'مجموعة المنصوري',            slug: 'almansoori',       industry: 'construction' },
  { nameEn: 'Sharjah Industrial FZE',     nameAr: 'الشارقة الصناعية',           slug: 'sharjah-ind',      industry: 'trading'      },
  { nameEn: 'Abu Dhabi Energy Services',  nameAr: 'أبوظبي لخدمات الطاقة',       slug: 'auh-energy',       industry: 'energy'       },
  { nameEn: 'Al Falasi Holding',          nameAr: 'مجموعة الفلاسي القابضة',    slug: 'alfalasi',         industry: 'real_estate'  },
  { nameEn: 'Gulf Aviation Catering',     nameAr: 'الخليج لتموين الطيران',     slug: 'gulf-aviation',    industry: 'aviation'     },
  { nameEn: 'Al Naboodah Construction',   nameAr: 'النابودة للإنشاءات',         slug: 'naboodah',         industry: 'construction' },
  { nameEn: 'Dubai Hospitality Group',    nameAr: 'مجموعة دبي للضيافة',        slug: 'dubai-hospitality',industry: 'hospitality'  },
  { nameEn: 'Ras Al Khaimah Cement',      nameAr: 'رأس الخيمة للأسمنت',         slug: 'rak-cement',       industry: 'construction' },
  { nameEn: 'Sharjah Telecom Solutions',  nameAr: 'الشارقة لحلول الاتصالات',   slug: 'sharjah-telecom',  industry: 'telecom'      },
  { nameEn: 'Al Habtoor Trading FZE',     nameAr: 'الحبتور للتجارة',            slug: 'habtoor-trade',    industry: 'trading'      },
  { nameEn: 'Emirates Auto Mall LLC',     nameAr: 'الإمارات لمعارض السيارات',  slug: 'auto-mall',        industry: 'retail'       },
  { nameEn: 'Al Tayer Marine Services',   nameAr: 'الطاير للخدمات البحرية',    slug: 'tayer-marine',     industry: 'logistics'    },
  { nameEn: 'Future Healthcare Group',    nameAr: 'مجموعة المستقبل للرعاية',   slug: 'future-health',    industry: 'healthcare'   },
  { nameEn: 'Dubai Tech Innovations LLC', nameAr: 'دبي للابتكارات التقنية',    slug: 'dubai-tech',       industry: 'tech'         },
  { nameEn: 'Al Owais Banking Services',  nameAr: 'العويس للخدمات المصرفية',   slug: 'alowais-banking',  industry: 'banking'      },
  { nameEn: 'Khalifa Industrial Park',    nameAr: 'مجمع خليفة الصناعي',         slug: 'khalifa-park',     industry: 'energy'       },
  { nameEn: 'Al Sayegh Jewellers FZE',    nameAr: 'الصايغ للمجوهرات',           slug: 'sayegh-jewel',     industry: 'retail'       },
  { nameEn: 'Sheikh Zayed Logistics LLC', nameAr: 'الشيخ زايد للخدمات اللوجستية', slug: 'zayed-logistics', industry: 'logistics'   },
  { nameEn: 'Al Ghurair Foods JLT',       nameAr: 'الغرير للأغذية',             slug: 'ghurair-foods',    industry: 'retail'       },
  { nameEn: 'Dubai Health Insurance Co.', nameAr: 'دبي لتأمين الصحة',           slug: 'dubai-health',     industry: 'healthcare'   },
  { nameEn: 'Mubadala Tech Ventures',     nameAr: 'مبادلة لتقنية المشاريع',     slug: 'mubadala-tech',    industry: 'tech'         },
  { nameEn: 'Sharjah Maritime Services',  nameAr: 'الشارقة للخدمات البحرية',   slug: 'sharjah-maritime', industry: 'logistics'    },
  { nameEn: 'Etisalat by Sky Telecom',    nameAr: 'سكاي تلكوم',                  slug: 'sky-telecom',      industry: 'telecom'      },
];

export const PEOPLE: SamplePerson[] = [
  { givenName: 'Mohammed', familyName: 'Al Marri',     givenNameAr: 'محمد',  familyNameAr: 'المرّي',     gender: 'm' },
  { givenName: 'Ahmed',    familyName: 'Al Maktoum',   givenNameAr: 'أحمد',  familyNameAr: 'آل مكتوم',   gender: 'm' },
  { givenName: 'Hassan',   familyName: 'Al Mansoori',  givenNameAr: 'حسن',   familyNameAr: 'المنصوري',   gender: 'm' },
  { givenName: 'Khalid',   familyName: 'Al Falasi',    givenNameAr: 'خالد',  familyNameAr: 'الفلاسي',    gender: 'm' },
  { givenName: 'Omar',     familyName: 'Al Habtoor',   givenNameAr: 'عمر',   familyNameAr: 'الحبتور',    gender: 'm' },
  { givenName: 'Saeed',    familyName: 'Al Tayer',     givenNameAr: 'سعيد',  familyNameAr: 'الطاير',     gender: 'm' },
  { givenName: 'Faisal',   familyName: 'Al Naboodah',  givenNameAr: 'فيصل',  familyNameAr: 'النابودة',   gender: 'm' },
  { givenName: 'Sultan',   familyName: 'Al Suwaidi',   givenNameAr: 'سلطان', familyNameAr: 'السويدي',    gender: 'm' },
  { givenName: 'Rashid',   familyName: 'Al Owais',     givenNameAr: 'راشد',  familyNameAr: 'العويس',     gender: 'm' },
  { givenName: 'Mansour',  familyName: 'Al Sayegh',    givenNameAr: 'منصور', familyNameAr: 'الصايغ',     gender: 'm' },
  { givenName: 'Saif',     familyName: 'Al Ghurair',   givenNameAr: 'سيف',   familyNameAr: 'الغرير',     gender: 'm' },
  { givenName: 'Tariq',    familyName: 'Al Khalifa',   givenNameAr: 'طارق',  familyNameAr: 'الخليفة',    gender: 'm' },
  { givenName: 'Aisha',    familyName: 'Al Marri',     givenNameAr: 'عائشة', familyNameAr: 'المرّي',     gender: 'f' },
  { givenName: 'Fatima',   familyName: 'Al Mansoori',  givenNameAr: 'فاطمة', familyNameAr: 'المنصوري',   gender: 'f' },
  { givenName: 'Mariam',   familyName: 'Al Maktoum',   givenNameAr: 'مريم',  familyNameAr: 'آل مكتوم',   gender: 'f' },
  { givenName: 'Layla',    familyName: 'Hassan',       givenNameAr: 'ليلى',  familyNameAr: 'حسن',         gender: 'f' },
  { givenName: 'Sara',     familyName: 'Al Tayer',     givenNameAr: 'سارة',  familyNameAr: 'الطاير',     gender: 'f' },
  { givenName: 'Hessa',    familyName: 'Al Falasi',    givenNameAr: 'حصة',   familyNameAr: 'الفلاسي',    gender: 'f' },
  { givenName: 'Noura',    familyName: 'Al Suwaidi',   givenNameAr: 'نورة',  familyNameAr: 'السويدي',    gender: 'f' },
  { givenName: 'Shamma',   familyName: 'Al Nahyan',    givenNameAr: 'شمّاء',  familyNameAr: 'آل نهيان',   gender: 'f' },
  { givenName: 'Salama',   familyName: 'Al Khalifa',   givenNameAr: 'سلامة', familyNameAr: 'الخليفة',    gender: 'f' },
  { givenName: 'Reem',     familyName: 'Al Habtoor',   givenNameAr: 'ريم',   familyNameAr: 'الحبتور',    gender: 'f' },
];

export function pickCompany(rng: Rng = defaultRng()): SampleCompany {
  return rng.pick(COMPANIES);
}

export function pickPerson(rng: Rng = defaultRng()): SamplePerson {
  return rng.pick(PEOPLE);
}

export function generateCompanyContact(
  company: SampleCompany,
  rng: Rng = defaultRng(),
): { person: SamplePerson; email: string; phone: string } {
  const person = pickPerson(rng);
  return {
    person,
    email: generateEmail(person.givenName, person.familyName, company.slug),
    phone: generateUaePhone(rng),
  };
}
